/**
 * Observable binding state: what this browser's device key is doing, and
 * whether the server is currently refusing us for want of a proof.
 *
 * ## Why a module store and not provider state
 *
 * Three unrelated realms read this — the session provider, the unbound banner
 * and the Sessions settings card — and the underlying facts (an IndexedDB
 * record, a transport-level 401) are not React state to begin with. A store
 * with `useSyncExternalStore` keeps the source of truth where it actually
 * lives, and keeps the provider free of the fetch-then-`setState`-in-effect
 * pattern the compiler warns about.
 *
 * ## The three answers, kept apart on purpose (ADR 0042)
 *
 * - **`bound`** — the *server's* verdict, read off `Session.bound`. Never
 *   inferred from whether we happen to hold a key: a client that decided for
 *   itself that it was bound is the silent fallback the ADR forbids.
 * - **`key`** — what this browser can do: `ready`, `absent` (storage works,
 *   there is no key — the common lost-key case) or `unsupported` (no
 *   WebCrypto, no IndexedDB, storage refused).
 * - **`proofFailure`** — the server refused a request for want of a proof and
 *   *kept the session alive*. This is what raises the in-place password
 *   dialog. It is emphatically not a sign-out.
 */

import {
  ensureDeviceKey,
  forgetCachedDeviceKey,
  loadDeviceKey,
  type DeviceKeyState,
} from '@/api/deviceKey';
import type { ProofFailureReason } from '@/api/sessionSignals';

export interface BindingState {
  /** `undefined` until the first look at storage resolves. */
  readonly key: DeviceKeyState | undefined;
  /** The server's answer, or `undefined` before the first `Session` body. */
  readonly bound: boolean | undefined;
  /** Set while a `proof_*` 401 is outstanding; cleared by a successful rebind. */
  readonly proofFailure: ProofFailureReason | null;
}

const INITIAL: BindingState = { key: undefined, bound: undefined, proofFailure: null };

let state: BindingState = INITIAL;
const listeners = new Set<() => void>();

function publish(next: BindingState): void {
  if (
    next.key === state.key &&
    next.bound === state.bound &&
    next.proofFailure === state.proofFailure
  ) {
    return;
  }
  state = next;
  for (const listener of [...listeners]) listener();
}

export function subscribeBinding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable snapshot — the same object until something actually changes. */
export function bindingSnapshot(): BindingState {
  return state;
}

/**
 * Generates the key if this profile has none, and records the answer.
 *
 * Idempotent and cheap after the first call. Run at boot rather than only on
 * the login route: the login page is where a *new* key is wanted (ADR 0044),
 * and a boot that finds none on an existing session is the lost-key case that
 * needs one generated before the rebind can enrol it. Both want the same call.
 */
export async function initBinding(): Promise<DeviceKeyState> {
  const next = await ensureDeviceKey();
  publish({ ...state, key: next });
  return next;
}

/** Re-reads storage without generating — after a broadcast from another tab. */
export async function refreshBindingKey(): Promise<DeviceKeyState> {
  forgetCachedDeviceKey();
  const next = await loadDeviceKey();
  publish({ ...state, key: next });
  return next;
}

/** Records the server's `bound` verdict from a `Session` body. */
export function noteBound(bound: boolean | undefined): void {
  publish({ ...state, bound });
}

/** A `proof_*` 401 survived the transport's retries. */
export function noteProofFailure(reason: ProofFailureReason): void {
  publish({ ...state, proofFailure: reason });
}

/** The rebind landed, or the user signed out: stop asking. */
export function clearProofFailure(): void {
  publish({ ...state, proofFailure: null });
}

/** Test seam: forget everything between cases. */
export function resetBindingState(): void {
  state = INITIAL;
  for (const listener of [...listeners]) listener();
}
